import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";
import PopoverHeader from "../../common/PopoverHeader";
import { TextField, Button } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import FitToPageHorizontal from "./img/FitToPageHorizontal";
import FitToPageVertical from "./img/FitToPageVertical";
import { FormattedMessage, useIntl } from "react-intl";
import withBasicFormatting, {
  BasicFormattingProps,
} from "./withBasicFormatting";
import { cloud } from "@vitalsource/vstui/colors";
import { ViewerProps } from "../../viewer-context/withViewer";
import PageView from "./PageView";
import { PlusCircleFill, MinusCircleFill } from "@vitalsource/vstui-icons";

const MENU_PADDING = 16;
const CONTAINER_WIDTH = `320px`;
const ZOOM_BUTTON_MARGIN = 8;

const MenuContainer = styled.div`
  width: ${CONTAINER_WIDTH};
`;

const ZoomContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${MENU_PADDING}px ${MENU_PADDING - ZOOM_BUTTON_MARGIN}px;
`;
const ZoomButtonContainer = styled.div`
  margin: 0 2px;
`;
const TextFieldContainer = styled.form`
  display: flex;
  padding: 0;
  align-items: center;
  div:first-of-type {
    margin: 0;
    width: 75px;
  }
  input {
    text-align: center;
  }
`;

const ZoomButton = createThemedComponent(Button, {
  Button_paddingHorizontal_withIconMedium: `${ZOOM_BUTTON_MARGIN}px`,
});

const fitToPage = (horizontal: boolean) => {
  return createThemedComponent(Button, {
    Button_paddingHorizontal_medium: horizontal ? "5px" : "17px",
    Button_minHeight_medium: "52",
    Button_borderColor_themePrimaryNaked: cloud.getShade(6),
    Button_borderColor_themePrimaryNaked_active: cloud.getShade(6),
    Button_borderColor_themePrimaryNaked_focus: cloud.getShade(6),
    Button_borderColor_themePrimaryNaked_hover: cloud.getShade(6),
  });
};
const FitButtonVertical = fitToPage(false);
const FitButtonHorizontal = fitToPage(true);

const HorizontalContainer = styled.div`
  background: ${cloud.getShade(6)};
  width: 64px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const VerticalContainer = styled.div`
  background: ${cloud.getShade(6)};
  width: 40px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MenuContentContainer = styled.div`
  padding: ${MENU_PADDING}px;
`;
const FitContainer = styled(MenuContentContainer)`
  display: flex;
  > div {
    margin-right: 15px;
  }
`;

function formatZoom(n: number) {
  return `${Math.round(n * 100)}%`;
}

const BasicFormatting = (props: BasicFormattingProps & ViewerProps) => {
  const intl = useIntl();
  const [zoomInputVal, setZoomInputVal] = useState("");

  const setFieldToCurrentZoom = useCallback(() => {
    setZoomInputVal(formatZoom(props.zoomLevel));
  }, [props.zoomLevel]);

  useEffect(() => {
    setFieldToCurrentZoom();
  }, [setFieldToCurrentZoom]);

  function submitZoom(e: FormEvent) {
    e.preventDefault();
    const val = parseInt(zoomInputVal, 10);
    if (!isNaN(val)) {
      const adjustedVal = val / 100;
      props.setZoomScale(adjustedVal);
      setZoomInputVal(formatZoom(adjustedVal));
    } else {
      setFieldToCurrentZoom();
    }
  }

  function zoomInputChanged(e: ChangeEvent<HTMLInputElement>) {
    setZoomInputVal(e.currentTarget.value);
  }

  return (
    <MenuContainer data-testid="basic_menu">
      <PopoverHeader>{intl.formatMessage({ id: "format.zoom" })}</PopoverHeader>
      <ZoomContainer>
        <ZoomButtonContainer>
          <ZoomButton
            onClick={props.decreaseZoom}
            naked
            label={intl.formatMessage({ id: "format.zoomOut" })}
            iconStart={<MinusCircleFill />}
          />
        </ZoomButtonContainer>
        <TextFieldContainer onSubmit={submitZoom}>
          <TextField
            labelHidden
            label={intl.formatMessage({ id: "format.zoom" })}
            type="text"
            value={zoomInputVal}
            onChange={zoomInputChanged}
          />
        </TextFieldContainer>
        <ZoomButtonContainer>
          <ZoomButton
            onClick={props.increaseZoom}
            naked
            label={intl.formatMessage({ id: "format.zoomIn" })}
            iconStart={<PlusCircleFill />}
          />
        </ZoomButtonContainer>
      </ZoomContainer>

      {props.bookData.vbkType === "pbk" && (
        <>
          <PopoverHeader>
            {intl.formatMessage({ id: "format.fitToPage" })}
          </PopoverHeader>
          <FitContainer>
            <FitButtonHorizontal
              onClick={props.fitToWidth}
              label={intl.formatMessage({ id: "format.fillWidth" })}
              naked
            >
              <HorizontalContainer>
                <FitToPageHorizontal />
              </HorizontalContainer>
            </FitButtonHorizontal>
            <FitButtonVertical
              onClick={props.fitToHeight}
              label={intl.formatMessage({ id: "format.fillHeight" })}
              naked
            >
              <VerticalContainer>
                <FitToPageVertical />
              </VerticalContainer>
            </FitButtonVertical>
          </FitContainer>
        </>
      )}

      {props.bookData.dpsAvailable && (
        <>
          <PopoverHeader>
            <FormattedMessage id="format.pageView" />
          </PopoverHeader>
          <MenuContentContainer>
            <PageView
              twoPageSpread={props.twoPageSpread}
              dpsOn={() => props.setDps(true)}
              dpsOff={() => props.setDps(false)}
            />
          </MenuContentContainer>
        </>
      )}
    </MenuContainer>
  );
};

export default withBasicFormatting(BasicFormatting);
